import { ApiError, apiRequest } from '../api/client';
import { fetchCapabilities, type Capabilities } from '../api/meta';
import { clearUserPermissions, publishUserPermissions } from '../store/persistence';
import type { CurrentUser } from './state';
import type { Permissions } from '../lib/types';

/**
 * The session: who is signed in, what they may do, and what this deployment offers.
 *
 * Auth is SERVER-SIDE. The httpOnly `connect.sid` cookie is the source of truth, and this
 * module only asks the server about it — there is no client-side notion of "logged in" beyond
 * what `GET /api/auth/me` last said. That is why `establish()` is the only way to learn the
 * answer, and why both `login()` and a page load funnel through it.
 *
 * THE ONE SIDE EFFECT IS A CONTRACT, NOT A CACHE. `sessionStorage["userPermissions"]` is
 * shared by the whole CONIGON suite: other apps write their own keys into that object and read
 * each other's. Publishing our payload under `documentation` is part of signing in, not an
 * optimisation, which is why it happens here rather than being left to a caller to remember.
 * The storage format itself lives in `store/persistence.ts`, with every other key this app
 * touches; `publishUserPermissions` replaces exactly one key and leaves the rest alone.
 *
 * CAPABILITIES RIDE ALONG because they have the same lifetime as the session and are read at
 * the same point in boot — before any chrome is drawn, so a feature this deployment does not
 * have is ABSENT rather than present-and-erroring (DEPLOYMENTS.md). A failed or unavailable
 * fetch resolves to `null`, which every gate reads as "unknown, assume present" — an older
 * server therefore behaves exactly as it did before the endpoint existed.
 */

/** What `GET /api/auth/me` answers. */
interface MeResponse {
  id: string;
  username: string;
  displayName?: string;
  /**
   * The signed-in account's avatar version, or null for none. On the USER half of this answer
   * and deliberately not inside `perm`: `perm` is what `publishUserPermissions` republishes
   * into the suite-shared `sessionStorage["userPermissions"]`, and an avatar is identity, not
   * permission. Optional so a server that predates the column just draws initials.
   */
  avatarV?: number | null;
  /**
   * This account's own team, or null (V0.50). On the USER half beside `avatarV` and for that
   * field's reason exactly: `perm` is what gets republished into the suite-shared
   * `sessionStorage["userPermissions"]`, and which team somebody is in is identity, not
   * permission. Optional, so a server that predates it simply reports no team.
   */
  groupId?: string | null;
  roles: string[];
  docButtons: Permissions['docButtons'];
  hiddenNodes: string[];
  hiddenTags: string[];
}

export interface SessionInfo {
  currentUser: CurrentUser | null;
  perm: Permissions | null;
}

const SIGNED_OUT: SessionInfo = { currentUser: null, perm: null };

/**
 * Ask the server who we are, and publish the answer to the suite.
 *
 * Never throws: a 401 is the ordinary "not signed in" answer, and a network failure at boot
 * has to leave the app on the login card rather than on a blank page.
 */
export async function establish(): Promise<SessionInfo> {
  try {
    const data = await apiRequest<MeResponse>('/api/auth/me');
    const info: SessionInfo = {
      currentUser: {
        id: data.id,
        username: data.username,
        displayName: data.displayName,
        avatarV: data.avatarV ?? null,
        groupId: data.groupId ?? null,
      },
      perm: {
        roles: data.roles,
        docButtons: data.docButtons,
        hiddenNodes: data.hiddenNodes,
        hiddenTags: data.hiddenTags,
      },
    };
    publishUserPermissions(info.perm);
    return info;
  } catch {
    return SIGNED_OUT;
  }
}

/**
 * Exchange a username and password for a session cookie.
 *
 * Returns whether the credentials were accepted and nothing else: the caller establishes the
 * session afterwards, so there is exactly one place that decides what being signed in means.
 * Wrong credentials are a 401, which is an answer rather than a fault — hence the ApiError
 * check instead of letting it propagate.
 */
export async function login(username: string, password: string): Promise<boolean> {
  try {
    await apiRequest('/api/auth/login', { method: 'POST', body: { username, password } });
    return true;
  } catch (err) {
    if (err instanceof ApiError) return false;
    return false;
  }
}

/**
 * End the session.
 *
 * The local key is cleared whatever the server said: if the POST failed we are still leaving,
 * and leaving a stale permission payload behind for the next person on this browser would be
 * worse than a session the server has not yet forgotten.
 *
 * Resolves to where the browser must go next, or null. A Keycloak session answers with
 * Keycloak's own logout address (V0.54.3), which ends the Keycloak session too and comes back
 * here; without it the next "Login with Keycloak" signed the same person straight back in.
 */
export async function end(): Promise<string | null> {
  let redirect: string | null = null;
  try {
    const res = await apiRequest<{ ok: boolean; redirect?: string }>('/api/auth/logout', { method: 'POST' });
    redirect = typeof res?.redirect === 'string' ? res.redirect : null;
  } catch {
    /* going anyway */
  }
  clearUserPermissions();
  return redirect;
}

/** What this DEPLOYMENT can do. `null` means "not asked / unknown", never "no AI". */
export async function capabilities(): Promise<Capabilities | null> {
  try {
    return await fetchCapabilities();
  } catch {
    return null;
  }
}
